import React from 'react';
import { useSearchParams } from 'react-router-dom';

import Modal from '@hero/ui-kit/components/Modal';
import PlusIcon from '@hero/ui-kit/icons/utility/PlusIcon';
import UploadIcon from '@hero/ui-kit/icons/utility/UploadIcon';
import CircularButton from '@hero/ui-kit/inputs/CircularButton';
import H from '@hero/ui-kit/typography/H';
import Layout from '../../components/Layout';
import LocationFilter from '../../components/LocationFilter';
import SplitButton from '../../components/SplitButton';
import { useUserStatusContext } from '../../context/UserStatus';
import CheckinMembersPage from './CheckinMembers';
import DischargedMemberList from './DischargedMemberList';
import DisengagedMemberList from './DisengagedMembers/List';
import OrganizationMemberList from './OrganizationMemberList';
import useListQueryParams from './OrganizationMemberList/useListQueryParams';
import useGetExportMembers from './api/useGetExportMembers';
import useGetOrganizationMembers from './api/useGetOrganizationMembers';
import CreateMemberForm from './components/CreateMemberForm';
import getMemberListFilters from './memberListFilters';
import useIsCareManager from '../../hooks/useIsCareManager';
import useDefaultCareManagerFilter from './useDefaultCareManagerFilter';
import memberSearchProps from './memberSearchProps';

const FleetManagement: React.FC = () => {
    const { permissions } = useUserStatusContext();
    const isCareManager = useIsCareManager();
    const [createMemberModal, setCreateMemberModal] = React.useState(false);
    const [isBulkMode, setIsBulkMode] = React.useState(false);
    const [selectedMemberIds, setSelectedMemberIds] = React.useState<Set<number>>(new Set());
    const csvModalControls = React.useState(false);
    const [searchParams] = useSearchParams();

    React.useEffect(() => {
        if (searchParams.get('openAddMember') === '1') {
            setCreateMemberModal(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { query } = useListQueryParams();

    useDefaultCareManagerFilter(isCareManager);

    const { filters, filterParams } = React.useMemo(
        () => getMemberListFilters(query.list_version),
        [query.list_version]
    );

    const { isLoading, isError, data } = useGetOrganizationMembers(query);
    const { exportMembers, loading } = useGetExportMembers(query, data?.total_count);

    const handleToggleBulkMode = React.useCallback(() => {
        setIsBulkMode((prev) => {
            if (prev && selectedMemberIds.size === 0) {
                return false;
            }
            if (!prev) {
                setSelectedMemberIds(new Set());
                return true;
            }
            return prev;
        });
    }, [selectedMemberIds.size]);

    const handleExitBulkMode = React.useCallback(() => {
        setIsBulkMode(false);
        setSelectedMemberIds(new Set());
    }, []);

    const headerElements = (
        <>
            <LocationFilter />
            {query.list_version !== 'checkin-members' ? (
                <>
                    <CircularButton
                        disabled={loading}
                        variant="secondary"
                        isLink={false}
                        onClick={exportMembers}
                        className="flex-center"
                        alt="Export Members"
                        styles={{ opacity: loading ? 0.5 : 1 }}
                    >
                        <UploadIcon size="utility" styles={{ transform: 'rotateZ(180deg)' }} />
                    </CircularButton>
                </>
            ) : null}
            <CircularButton
                variant="secondary"
                isLink={false}
                onClick={(e) => {
                    e.stopPropagation();
                    setCreateMemberModal(true);
                }}
                className="flex-center"
                alt="Add Member"
            >
                <PlusIcon size="utility" />
            </CircularButton>

            {/* HWM-23020: Bulk Edit only assigns Care Managers to members, so it requires
                the Care Manager module EDIT bit (Supervisor / Admin). Standard / Read Only
                cannot assign CMs and must not see this control. */}
            {permissions.careManager.careManagerEdit &&
            (!query.list_version || query.list_version === 'active') ? (
                <SplitButton
                    label="Bulk Edit"
                    onClick={handleToggleBulkMode}
                    active={isBulkMode}
                    locked={isBulkMode && selectedMemberIds.size > 0}
                    dropdownItems={[
                        {
                            label: 'Bulk Assign via CSV',
                            onClick: () => csvModalControls[1](true)
                        }
                    ]}
                />
            ) : null}
        </>
    );

    return (
        <>
            <Layout
                title="Members"
                {...memberSearchProps}
                removeQueryParamsOnSearch={['limit', 'offset', 'online_status', 'device_status']}
                filters={filters}
                filterParams={filterParams}
                additionalElement={isCareManager ? undefined : headerElements}
            >
                {query.list_version === 'disengaged' ? (
                    <DisengagedMemberList data={data} isError={isError} isLoading={isLoading} />
                ) : query.list_version === 'discharged_and_cancelled' ? (
                    <DischargedMemberList data={data} isError={isError} isLoading={isLoading} />
                ) : query.list_version === 'checkin-members' ? (
                    <CheckinMembersPage />
                ) : (
                    <OrganizationMemberList
                        data={data}
                        isError={isError}
                        isLoading={isLoading}
                        isBulkMode={isBulkMode}
                        selectedMemberIds={selectedMemberIds}
                        setSelectedMemberIds={setSelectedMemberIds}
                        onExitBulkMode={handleExitBulkMode}
                        csvModalControls={csvModalControls}
                    />
                )}
            </Layout>

            {createMemberModal && (
                <Modal
                    isCancelable
                    onClose={() => {
                        setCreateMemberModal(false);
                    }}
                    styles={{ padding: '0' }}
                    width="page"
                >
                    <H role="h5">Add Member</H>
                    <CreateMemberForm onSuccess={() => setCreateMemberModal(false)} />
                </Modal>
            )}
        </>
    );
};

export default FleetManagement;
